import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Heart, MapPin, Clock, Eye, MessageCircle, Share2, 
  Gift, Star, Sparkles, ExternalLink, Users, Compass,
  BookOpen, Palette, Briefcase, ChevronRight
} from 'lucide-react';
import { HeartAlchemyIcon, LotusIcon, SpiralIcon, CrystalIcon } from './icons/AlchemyIcons';

// Category icon mapping
const CATEGORY_ICONS = {
  coaching: Compass,
  mentoring: Users,
  healing: HeartAlchemyIcon,
  teaching: BookOpen,
  creative: Palette,
  professional: Briefcase,
  spiritual: LotusIcon,
  nvc: MessageCircle,
  other: Gift
};

// Color gradients for cards
const GRADIENTS = {
  amber: 'from-amber-600/20 via-amber-500/10 to-orange-600/20',
  teal: 'from-teal-600/20 via-teal-500/10 to-emerald-600/20',
  rose: 'from-rose-600/20 via-rose-500/10 to-pink-600/20',
  blue: 'from-blue-600/20 via-blue-500/10 to-indigo-600/20',
  violet: 'from-violet-600/20 via-violet-500/10 to-purple-600/20',
  slate: 'from-slate-600/20 via-slate-500/10 to-gray-600/20',
  purple: 'from-purple-600/20 via-purple-500/10 to-violet-600/20',
  emerald: 'from-emerald-600/20 via-emerald-500/10 to-teal-600/20',
  orange: 'from-orange-600/20 via-orange-500/10 to-amber-600/20'
};

const BORDER_COLORS = {
  amber: 'border-amber-500/30 hover:border-amber-500/50',
  teal: 'border-teal-500/30 hover:border-teal-500/50',
  rose: 'border-rose-500/30 hover:border-rose-500/50',
  blue: 'border-blue-500/30 hover:border-blue-500/50',
  violet: 'border-violet-500/30 hover:border-violet-500/50',
  slate: 'border-slate-500/30 hover:border-slate-500/50',
  purple: 'border-purple-500/30 hover:border-purple-500/50',
  emerald: 'border-emerald-500/30 hover:border-emerald-500/50',
  orange: 'border-orange-500/30 hover:border-orange-500/50'
};

const TEXT_COLORS = {
  amber: 'text-amber-400',
  teal: 'text-teal-400',
  rose: 'text-rose-400',
  blue: 'text-blue-400',
  violet: 'text-violet-400',
  slate: 'text-slate-400',
  purple: 'text-purple-400',
  emerald: 'text-emerald-400',
  orange: 'text-orange-400'
};

// Credibility level badges
const CREDIBILITY_BADGES = {
  newcomer: { label: 'Newcomer', color: 'bg-stone-500/20 text-stone-400 border-stone-500/30' },
  emerging_giver: { label: 'Emerging Giver', color: 'bg-teal-500/20 text-teal-400 border-teal-500/30' },
  active_contributor: { label: 'Active Contributor', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  trusted_giver: { label: 'Trusted Giver', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  community_pillar: { label: 'Community Pillar', color: 'bg-gradient-to-r from-amber-500/20 to-rose-500/20 text-amber-300 border-amber-500/30' }
};

function getCredibilityLevel(score) {
  if (score >= 500) return 'community_pillar';
  if (score >= 200) return 'trusted_giver';
  if (score >= 100) return 'active_contributor';
  if (score >= 25) return 'emerging_giver';
  return 'newcomer';
}

export default function OfferingCard({ offering, variant = 'card', onContact, onShare }) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  
  const color = offering.cover_color || 'amber';
  const gradient = GRADIENTS[color] || GRADIENTS.amber;
  const borderColor = BORDER_COLORS[color] || BORDER_COLORS.amber;
  const textColor = TEXT_COLORS[color] || TEXT_COLORS.amber;
  const CategoryIcon = CATEGORY_ICONS[offering.category] || Gift;
  const credibilityLevel = getCredibilityLevel(offering.giver_credibility || 0);
  const credibilityBadge = CREDIBILITY_BADGES[credibilityLevel];

  const handleClick = () => {
    navigate(`/offerings/${offering._id}`);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/offerings/${offering._id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: offering.title,
          text: `${offering.giver_name} is offering: ${offering.title}`,
          url: shareUrl
        });
      } catch (err) {
        // Fallback to clipboard
        navigator.clipboard.writeText(shareUrl);
        if (onShare) onShare();
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      if (onShare) onShare();
    }
  };

  if (variant === 'featured') {
    return (
      <Card 
        className={`relative overflow-hidden bg-gradient-to-br ${gradient} border ${borderColor} cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl`}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid={`offering-card-${offering._id}`}
      >
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
          <SpiralIcon className="w-full h-full" />
        </div>
        
        <CardContent className="p-6 relative z-10">
          {/* Category Badge */}
          <div className="flex items-center justify-between mb-4">
            <Badge className={`${textColor} bg-black/20 border-0`}>
              <CategoryIcon className="w-3 h-3 mr-1" />
              {offering.category_label}
            </Badge>
            {offering.gratitude_count > 0 && (
              <div className="flex items-center gap-1 text-rose-400 text-xs">
                <Heart className="w-3 h-3 fill-current" />
                {offering.gratitude_count}
              </div>
            )}
          </div>
          
          {/* Title */}
          <h3 className="text-xl font-medium text-white mb-2 line-clamp-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {offering.title}
          </h3>
          
          {/* Description */}
          <p className="text-stone-300 text-sm mb-4 line-clamp-2">
            {offering.description}
          </p>
          
          {/* Giver Info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-medium border border-white/20`}>
                {offering.giver_name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm text-white">{offering.giver_name}</p>
                <Badge className={`text-[10px] ${credibilityBadge.color}`}>
                  {credibilityBadge.label}
                </Badge>
              </div>
            </div>
            
            <ChevronRight className={`w-5 h-5 ${textColor} transition-transform ${isHovered ? 'translate-x-1' : ''}`} />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Default card variant
  return (
    <Card 
      className={`relative overflow-hidden bg-stone-900/50 border ${borderColor} cursor-pointer transition-all duration-300 hover:shadow-lg group`}
      onClick={handleClick}
      data-testid={`offering-card-${offering._id}`}
    >
      {/* Top Gradient Bar */}
      <div className={`h-1 bg-gradient-to-r ${gradient.replace('/20', '/60').replace('/10', '/40')}`} />
      
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center`}>
              <CategoryIcon className={`w-5 h-5 ${textColor}`} />
            </div>
            <div>
              <Badge className={`text-xs bg-black/30 ${textColor} border-0`}>
                {offering.category_label}
              </Badge>
              <div className="flex items-center gap-2 mt-1">
                {offering.location === 'remote' && (
                  <span className="text-[10px] text-stone-500">🌐 Remote</span>
                )}
                {offering.location === 'local' && (
                  <span className="text-[10px] text-stone-500 flex items-center">
                    <MapPin className="w-2 h-2 mr-0.5" />{offering.area || 'Local'}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShare}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-stone-400 hover:text-white"
          >
            <Share2 className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Title */}
        <h3 className="text-lg font-medium text-white mb-2 line-clamp-2 group-hover:text-amber-200 transition-colors">
          {offering.title}
        </h3>
        
        {/* Description */}
        <p className="text-stone-400 text-sm mb-4 line-clamp-3">
          {offering.description}
        </p>
        
        {/* Tags */}
        {offering.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {offering.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-800 text-stone-400">
                #{tag}
              </span>
            ))}
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-stone-800">
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-xs font-medium`}>
              {offering.giver_name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-xs text-white">{offering.giver_name}</p>
              <div className="flex items-center gap-1">
                <Star className="w-2 h-2 text-amber-400" />
                <span className="text-[10px] text-stone-500">{offering.giver_credibility || 0} credibility</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3 text-stone-500 text-xs">
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />{offering.views || 0}
            </span>
            {offering.gratitude_count > 0 && (
              <span className="flex items-center gap-1 text-rose-400">
                <Heart className="w-3 h-3 fill-current" />{offering.gratitude_count}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Compact card for lists
export function OfferingCardCompact({ offering }) {
  const navigate = useNavigate();
  const color = offering.cover_color || 'amber';
  const textColor = TEXT_COLORS[color] || TEXT_COLORS.amber;
  const CategoryIcon = CATEGORY_ICONS[offering.category] || Gift;

  return (
    <div 
      className="flex items-center gap-4 p-3 bg-stone-800/30 rounded-lg border border-stone-700 hover:border-stone-600 cursor-pointer transition-all"
      onClick={() => navigate(`/offerings/${offering._id}`)}
    >
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${GRADIENTS[color]} flex items-center justify-center flex-shrink-0`}>
        <CategoryIcon className={`w-5 h-5 ${textColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-white truncate">{offering.title}</h4>
        <p className="text-xs text-stone-500">by {offering.giver_name}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-stone-500" />
    </div>
  );
}
